import { CheckCircle } from "@mui/icons-material";
import { Alert, FormGroup, FormLabel } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Form } from "react-final-form";
import { Text } from "../../../common/components/input/Text";
import { LoadingButton } from "../../../common/components/LoadingButton";
import { NetworkRequestState } from "../../../common/models";
import { ApiRouteHelpers } from "../../../common/root/apiRoutes";
import { UpdateOrganizationGoogleAnalyticsWebPropertyDto } from "../../../common/service/server/controller/OrganizationController";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationDto } from "../../../common/service/server/dto/OrganizationDto";
import { Callout, CalloutVariant } from "../../components/Callout";
import { DataRecipientLogo } from "../../components/DataRecipientLogo";
import { useAuthorizations } from "../../hooks/useAuthorizations";
import { requiredGAMeasurementId, requiredGAWebPropertyId } from "../../util/validation";
import { UpdatePCGoogleAnalyticsMeasurementIdDto } from "../../../common/service/server/controller/PrivacyCenterController";
import { InvalidateFn } from "../../../common/hooks/api";
import { PrivacyCenterDto } from "../../../common/service/server/dto/PrivacyCenterDto";

type OrganizationGoogleAnalyticsFormProps = {
  organization: OrganizationDto;
  googleAnalytics: OrganizationDataRecipientDto;
  onUpdateGAPropertyId: (values: UpdateOrganizationGoogleAnalyticsWebPropertyDto) => void;
  saveOnAuthorizeGaWebProperty?: () => void;
  showDoItLater: boolean;
  hasAuthorizeGoogleAnalyticsTask?: boolean;
  addAuthorizeGoogleAnalyticsTask?: () => Promise<void>;
  addAuthorizeGoogleAnalyticsTaskRequest?: NetworkRequestState;
  privacyCenter: PrivacyCenterDto;
  onUpdateGAMeasurementId: (values: UpdatePCGoogleAnalyticsMeasurementIdDto) => void;
  onErrorStateUpdate: (hasErrors: boolean) => void;
  loading?: boolean;
  invalidateOrg?: InvalidateFn;
};

export function OrganizationGoogleAnalyticsForm({
  organization,
  googleAnalytics,
  onUpdateGAPropertyId,
  hasAuthorizeGoogleAnalyticsTask,
  saveOnAuthorizeGaWebProperty,
  showDoItLater,
  addAuthorizeGoogleAnalyticsTask,
  addAuthorizeGoogleAnalyticsTaskRequest,
  privacyCenter,
  onUpdateGAMeasurementId,
  onErrorStateUpdate,
  invalidateOrg,
  loading = false,
}: OrganizationGoogleAnalyticsFormProps) {
  const {
    authorizations,
    actions: { unauthorizeGoogleAnalytics },
  } = useAuthorizations(organization.id);

  const [hasGAPropertyIdError, setHasGAPropertyIdError] = useState<boolean>(false);
  const [hasGAMeasurementIdError, setHasGAMeasurementIdError] = useState<boolean>(false);

  // Track locally what the user entered for a GA Property ID to validate that it does not duplicate the Measurement ID.
  // The state of these forms isn't otherwise tracked on the client. Each Form manages its own state and update callback
  // to the backend so this is the one piece of info we have to push upwards to this level of the component.
  const [gaPropertyIdForValidation, setGAPropertyIdForValidation] = useState<string>(
    organization.gaWebProperty,
  );

  useEffect(() => {
    onErrorStateUpdate(hasGAPropertyIdError || hasGAMeasurementIdError);
  }, [onErrorStateUpdate, hasGAPropertyIdError, hasGAMeasurementIdError]);

  const handleAuthorizeGoogle = async () => {
    // Invalidate the React Query organization cache before sending the user through the Google OAuth flow.
    if (invalidateOrg) {
      await invalidateOrg();
    }
    window.location.href = ApiRouteHelpers.organization.authorization.google(organization.id);
  };

  return (
    <div className="flex-row align-items-start">
      <div className="mr-lg">
        <DataRecipientLogo dataRecipient={googleAnalytics} />
      </div>

      <div>
        <h3 className="text-t3 m-0 text-weight-bold">Google Analytics</h3>

        <ol className="custom p-0">
          <li className="flex-row mb-md">
            <div>
              <Form
                initialValues={{ webProperty: organization.gaWebProperty }}
                onSubmit={onUpdateGAPropertyId}
                validate={(values) => {
                  const validationResults = requiredGAWebPropertyId(values);
                  setHasGAPropertyIdError((validationResults.webProperty ?? "") !== "");
                  setGAPropertyIdForValidation(values.webProperty);
                  return validationResults;
                }}
                render={({ handleSubmit, errors }) => {
                  return (
                    <>
                      <FormLabel className="text-bold">
                        Add your GA Property ID for automatic processing of opt-outs on your website
                      </FormLabel>
                      <Text
                        label="Google Analytics Property ID"
                        helperText="Your GA Property ID is 9 numbers (e.g. 123456789). To find it, login to your Google Analytics account. To find it, log in to your Google Analytics Admin page. At the top of your screen use the Search Bar to search for 'Property ID' and click 'Copy.'"
                        size="small"
                        field="webProperty"
                        variant="outlined"
                        disabled={Boolean(authorizations?.hasGoogle && organization?.gaWebProperty)}
                        onChange={handleSubmit}
                        required
                      />
                      {errors?.webProperty && (
                        <div className="flex-row">
                          <Alert severity="error">{errors.webProperty}</Alert>
                        </div>
                      )}
                    </>
                  );
                }}
              />
            </div>
          </li>

          <li className="flex-row mb-md">
            <div>
              <Form
                initialValues={{ gaMeasurementId: privacyCenter?.gaMeasurementId }}
                onSubmit={onUpdateGAMeasurementId}
                validate={(values) => {
                  const validationResults = requiredGAMeasurementId(
                    values,
                    gaPropertyIdForValidation,
                  );
                  setHasGAMeasurementIdError((validationResults.gaMeasurementId ?? "") !== "");
                  return validationResults;
                }}
                render={({ handleSubmit, errors }) => {
                  return (
                    <>
                      <FormLabel className="text-bold">
                        Add your GA Measurement ID for automatic processing of opt-outs on your
                        website
                      </FormLabel>

                      <Text
                        label="Google Analytics Measurement ID"
                        helperText='Your Measurement ID is a unique identifier for a website within Google Analytics. It looks like "G-" followed by a combination of letters and/or numbers (e.g. G-ABC123DEF4). To find it, log in to your Google Analytics Admin page. At the top of your screen use the Search Bar to search for "Measurement ID" and click "Copy."'
                        size="small"
                        field="gaMeasurementId"
                        variant="outlined"
                        onChange={handleSubmit}
                        required
                      />

                      {errors?.gaMeasurementId && (
                        <div className="flex-row">
                          <Alert severity="error">{errors.gaMeasurementId}</Alert>
                        </div>
                      )}
                    </>
                  );
                }}
              />
            </div>
          </li>

          <li className="flex-row  mb-md">
            <div className="w-100">
              <FormGroup className="mb-md">
                <FormLabel className="text-bold">
                  Connect your Google Analytics account so your team can automatically process
                  consumer data deletion requests for Google Analytics.{" "}
                  <span className="text-weight-bold">
                    Note that a Google Analytics admin login is required.
                  </span>
                </FormLabel>
              </FormGroup>

              <div className="flex-container">
                {authorizations?.hasGoogle ? (
                  <div className="flex-column">
                    <div className="flex-row flex-center-vertical mb-md">
                      <CheckCircle className="email-status-ok mr-sm" />
                      <span>Google Analytics is Authorized</span>
                    </div>

                    <LoadingButton
                      variant="contained"
                      onClick={unauthorizeGoogleAnalytics.fetch}
                      loading={unauthorizeGoogleAnalytics.request.running}
                    >
                      Disconnect Google Analytics
                    </LoadingButton>
                  </div>
                ) : (
                  <div>
                    <div className="flex-row">
                      <LoadingButton
                        loading={loading}
                        variant="contained"
                        onClick={() => {
                          saveOnAuthorizeGaWebProperty ? saveOnAuthorizeGaWebProperty() : null;
                          handleAuthorizeGoogle();
                        }}
                      >
                        Authorize Google Analytics
                      </LoadingButton>

                      {!hasAuthorizeGoogleAnalyticsTask && showDoItLater && (
                        <LoadingButton
                          variant="text"
                          color="secondary"
                          onClick={addAuthorizeGoogleAnalyticsTask}
                          loading={addAuthorizeGoogleAnalyticsTaskRequest?.running}
                        >
                          Do this later
                        </LoadingButton>
                      )}
                    </div>

                    {hasAuthorizeGoogleAnalyticsTask && showDoItLater && (
                      <Callout className="mt-mdlg" padding="md" variant={CalloutVariant.Yellow}>
                        No problem! We’ll add this to your to-do list and send you periodic
                        reminders to set this up. This item will be closed automatically once you
                        authorize an account.
                      </Callout>
                    )}
                  </div>
                )}
              </div>
            </div>
          </li>
        </ol>
      </div>
    </div>
  );
}
